import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layers, Tags, Package, LineChart, ArrowRight, History } from "lucide-react";
import { apiClient } from "@/lib/api";
import { composeAutoName, composeIdentityDots, isNameOverride } from "@/lib/composeItemName";

/**
 * Home — Step 1 rebuild.
 * Three primary buttons + one secondary (Analytics).
 *
 * "Continue" pill — surfaces the user's last visited context so they can
 * resume in one tap:
 *   • If BOTH `cdi.lastCollectionId` AND `cdi.lastItemId` are present in
 *     localStorage (last visit was an Item Detail screen), the pill reads
 *     "Continue: {collectionName} → {itemName}" and links to
 *     /items/{itemId}.
 *   • If only `cdi.lastCollectionId` is present (last visit was a
 *     Collection Detail screen), the pill reads "Continue: {collectionName}"
 *     and links to /collections/{collectionId}.
 *   • If neither key is present, or the stored ids 404, the pill is hidden
 *     and the stale keys are removed.
 */
export default function Home() {
  const buttons = [
    { to: "/collections", label: "Collections", Icon: Layers, testid: "home-go-collections" },
    { to: "/categories",  label: "Categories",  Icon: Tags,   testid: "home-go-categories"  },
    { to: "/items",       label: "Items",       Icon: Package,  testid: "home-go-items"       },
    { to: "/analytics",   label: "Portfolio Analytics",   Icon: LineChart, testid: "home-go-analytics" },
  ];

  // recent: { kind: "item" | "collection", to, collectionName, itemName? }
  const [recent, setRecent] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let lastCollectionId = "";
    let lastItemId = "";
    try {
      lastCollectionId = localStorage.getItem("cdi.lastCollectionId") || "";
      lastItemId = localStorage.getItem("cdi.lastItemId") || "";
    } catch (_e) { /* noop */ }
    if (!lastCollectionId && !lastItemId) return;

    (async () => {
      // Resolve the collection name (needed for both pill variants).
      let collectionName = "";
      if (lastCollectionId) {
        try {
          const { data } = await apiClient.get(`/collections/${lastCollectionId}`);
          collectionName = data?.name || "";
        } catch {
          // Stale / deleted collection — wipe both keys so the pill stops
          // showing on future loads.
          try {
            localStorage.removeItem("cdi.lastCollectionId");
            localStorage.removeItem("cdi.lastItemId");
          } catch (_e) { /* noop */ }
          return;
        }
      }
      // If a last-item is set, try to resolve it. On 404 we degrade to the
      // collection-only pill (keep the collection key, drop the item key).
      if (lastItemId) {
        try {
          const { data } = await apiClient.get(`/items/${lastItemId}`);
          if (cancelled) return;
          const itemName =
            data?.item_data?.["Item Name"] ||
            data?.item_name ||
            data?.name ||
            "Untitled";
          // If the item's actual collection has changed since we recorded
          // it, prefer the item's current collection for accuracy. We also
          // capture the full collection object so the pill can determine
          // the composition rule (Wine / Whisky / Spirits / Other) via
          // the shared composeAutoName helper (P8 / P11).
          let collectionObj = null;
          const resolvedCollectionId = data?.collection_id || lastCollectionId;
          if (resolvedCollectionId) {
            try {
              const colRes = await apiClient.get(`/collections/${resolvedCollectionId}`);
              collectionObj = colRes.data || null;
              if (collectionObj?.name) collectionName = collectionObj.name;
            } catch { /* noop — fall back to whatever collectionName we already have */ }
          }
          setRecent({
            kind: "item",
            to: `/items/${data.id}`,
            collectionName: collectionName || "Collection",
            itemName,
            itemId: data.id,
            itemData: data.item_data || {},
            collection: collectionObj,
          });
          return;
        } catch {
          // Stale item — drop the item key and fall through to the
          // collection-only pill if we have a collection name.
          try { localStorage.removeItem("cdi.lastItemId"); } catch (_e) { /* noop */ }
        }
      }
      if (!cancelled && lastCollectionId && collectionName) {
        setRecent({
          kind: "collection",
          to: `/collections/${lastCollectionId}`,
          collectionName,
        });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div
      className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20"
      data-testid="home-page"
      style={{ overflowX: "hidden", transform: "none" }}
    >
      <p className="text-sm vp-muted mb-6">Organise, track and analyse your collection — all in one place.</p>

      {recent && (
        <Link
          to={recent.to}
          data-testid="home-continue-pill"
          data-testid-kind={recent.kind}
          title={
            recent.kind === "item"
              ? `Resume ${recent.itemName} in ${recent.collectionName}`
              : `Return to ${recent.collectionName}`
          }
          className="inline-flex items-center gap-2 mb-10 px-3 py-1.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--accent))] hover:bg-[hsl(var(--muted))] transition-colors duration-fast group max-w-full"
        >
          <History size={13} className="text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))] shrink-0" />
          <span className="text-[10px] uppercase tracking-wider font-medium vp-muted shrink-0">Continue:</span>
          <span
            className="text-xs font-medium truncate max-w-[24ch]"
            data-testid="home-continue-name"
          >
            {recent.collectionName}
          </span>
          {recent.kind === "item" && (
            <>
              <span className="text-xs vp-muted shrink-0">→</span>
              {/* Metadata reconnect — dot-joined identity + folded
                  Region. Rules:
                    • Primary  = raw Item Name on override, composed
                      auto-name otherwise (fallback to raw).
                    • Secondary = `Producer · Appellation · Vintage`
                      identity (via composeIdentityDots), with the
                      item's Region appended as an extra dot-segment
                      (option `i` — no separate 3rd line).
                    • MANUAL pill sits between primary and secondary
                      when Name Override is on.
                  Reuses the shared helpers so Home / Items list /
                  ItemEntry stay in lock-step. */}
              {(() => {
                const override = isNameOverride(recent.itemData);
                const composed = composeAutoName(recent.itemData, recent.collection);
                const identity = composeIdentityDots(recent.itemData, recent.collection);
                const rawName = String(recent.itemName || "").trim();
                const primary = override
                  ? (rawName || composed)
                  : (composed || rawName);
                const region = String((recent.itemData || {})["Region"] || "").trim();
                const secondary = [identity, region].filter(Boolean).join(" · ");
                return (
                  <span
                    className="min-w-0 flex flex-col leading-tight"
                    data-testid="home-continue-item-name"
                  >
                    <span
                      className="text-xs font-medium truncate max-w-[24ch]"
                      data-testid={`cb-dashboard-composed-name-${recent.itemId}`}
                    >
                      {primary || "Untitled"}
                    </span>
                    {(override || secondary) ? (
                      <span className="mt-0.5 inline-flex items-center gap-1 min-w-0">
                        {override ? (
                          <span
                            data-testid={`cb-dashboard-manual-pill-${recent.itemId}`}
                            title="Item Name is manually overridden."
                            className="text-[9px] uppercase tracking-wider border border-[hsl(var(--gold))] text-[hsl(var(--gold))] rounded-full px-1"
                          >
                            Manual
                          </span>
                        ) : null}
                        {secondary ? (
                          <span className="text-[11px] vp-muted truncate max-w-[32ch]">
                            {secondary}
                          </span>
                        ) : null}
                      </span>
                    ) : null}
                  </span>
                );
              })()}
            </>
          )}
          <ArrowRight size={12} className="vp-muted shrink-0 group-hover:translate-x-0.5 transition-transform duration-fast" />
        </Link>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-5 vp-stagger">
        {buttons.map(({ to, label, Icon, testid }) => (
          <Link
            key={to}
            to={to}
            data-testid={testid}
            className="vp-card vp-card-hover pt-[38px] pb-[26px] px-8 flex flex-col items-start gap-4 group transition-colors duration-fast home-tile"
          >
            <div className="w-12 h-12 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--text-muted))] flex items-center justify-center home-tile-icon transition-colors duration-fast mb-[6px]">
              <Icon
                className="h-7 w-7"
                strokeWidth={2}
                fill="none"
                stroke="currentColor"
              />
            </div>
            <div className="text-xl font-semibold tracking-tight home-tile-title transition-colors duration-fast mb-[6px]">{label}</div>
            <span className="inline-flex items-center gap-1 mt-auto home-tile-go transition-colors duration-fast group-hover:translate-x-0.5" style={{ fontSize: "0.9rem", letterSpacing: "0.2px", color: "hsl(var(--text-muted))" }}>
              Go <ArrowRight size={12} />
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
